import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle2, XCircle, FileJson, MessageCircle, AlertCircle, Download, FileSpreadsheet, Plus, Settings, Trash, Eye, EyeOff, FileText, File, Search, Archive, Pencil, Bold, Italic, RotateCcw, Check } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from "recharts";
import { TalkContribution, TalkDebate, TalkReport, TalkDebateStatus, useTalkStore } from "@/data/talkStore";
import { getCmsLayout, publishCmsLayout, CmsLayout } from "@/data/cmsStore";
import { useSettingsStore } from "@/data/settingsStore";
import html2canvas from "html2canvas";
import { useToast } from "@/hooks/use-toast";
import { generateExport, exportDebateAnalysisReport } from "@/utils/exportUtils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

function getQuestionOfMonth(d: Date) {
  return Math.ceil(d.getDate() / 7);
}

function getWeekNumber(d: Date) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(),0,1));
  return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1)/7);
}

export default function AdminTalkPage() {
  const { toast } = useToast();
  const {
    debates,
    contributions,
    likesEnabled,
    fetchDebates,
    fetchContributions,
    createDebate,
    updateDebate,
    adminValidateContribution,
    adminEditContributionText,
    setTalkLikesEnabled,
    saveReport,
    reports,
    deleteDebate,
    deleteContribution,
    adminUpdateContribution
  } = useTalkStore();

  const [activeDebateId, setActiveDebateId] = useState<string>("");
  const [layout, setLayout] = useState<CmsLayout>([]);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newTitleAr, setNewTitleAr] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newDescAr, setNewDescAr] = useState("");
  
  const [searchQuery, setSearchQuery] = useState("");
  const [activeMainTab, setActiveMainTab] = useState("en_ligne");
  const [filterYear, setFilterYear] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterQ, setFilterQ] = useState("all");

  const [debConclusion, setDebConclusion] = useState("");
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<"excel" | "word" | "pdf">("pdf");
  const [includeStats, setIncludeStats] = useState(true);
  const [includeWatermark, setIncludeWatermark] = useState(true);

  const settingsObj = useSettingsStore(state => state.settings);
  const globalFormat = settingsObj['kaynin_talk_global_format'] || 'font-normal text-foreground';
  const setGlobalFormat = (format: string) => useSettingsStore.getState().updateSetting('kaynin_talk_global_format', format);

  const [editModeId, setEditModeId] = useState<string | null>(null);
  const [editText, setEditText] = useState<string>("");
  const [editFormat, setEditFormat] = useState<string>("font-normal text-foreground");
  const [editDebateId, setEditDebateId] = useState<string | null>(null);
  const [editDebateTitle, setEditDebateTitle] = useState("");
  const [editDebateTitleAr, setEditDebateTitleAr] = useState("");
  const [editDebateDesc, setEditDebateDesc] = useState("");
  const [editDebateDescAr, setEditDebateDescAr] = useState("");
  const [isEditDebateOpen, setIsEditDebateOpen] = useState(false);

  const [editProposedId, setEditProposedId] = useState<string | null>(null);
  const [editProposedText, setEditProposedText] = useState("");
  const [editProposedDomain, setEditProposedDomain] = useState("");
  const [isEditProposedOpen, setIsEditProposedOpen] = useState(false);
  const [proposedFilter, setProposedFilter] = useState("all"); 

  const toggleLikes = () => {
    const newVal = !likesEnabled;
    setTalkLikesEnabled(newVal);
    toast({ title: newVal ? "Likes activés" : "Likes désactivés", description: "La visibilité des likes sur les commentaires a été modifiée." });
  };

  const proposedQuestions = useMemo(() => {
    return contributions.filter(c => c.proposedQuestion && c.proposedQuestion.trim() !== "");
  }, [contributions]);

  const filteredProposedQuestions = useMemo(() => {
     let list = proposedQuestions;
     const now = new Date();
     
     if (proposedFilter === 'week') {
         list = list.filter(c => getWeekNumber(new Date(c.createdAt)) === getWeekNumber(now) && new Date(c.createdAt).getFullYear() === now.getFullYear());
     } else if (proposedFilter === 'month') {
         list = list.filter(c => new Date(c.createdAt).getMonth() === now.getMonth() && new Date(c.createdAt).getFullYear() === now.getFullYear());
     } else if (proposedFilter === 'year') {
         list = list.filter(c => new Date(c.createdAt).getFullYear() === now.getFullYear());
     }
     
     return list.sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [proposedQuestions, proposedFilter]);

  const handleExportProposed = (format: "pdf" | "word" | "excel") => {
    toast({ title: `Export ${format.toUpperCase()}`, description: "Génération en cours..." });
    setTimeout(() => {
       if (format === "excel") {
         const headers = ["Auteur", "Date", "Domaine", "Question"];
         const csvRows = filteredProposedQuestions.map(c => [
            `"${c.authorName.replace(/"/g, '""')}"`,
            `"${new Date(c.createdAt).toLocaleDateString('fr-FR')}"`,
            `"${(c.proposedDomain || "Non précisé").replace(/"/g, '""')}"`,
            `"${(c.proposedQuestion || "").replace(/"/g, '""')}"`
         ].join(","));
         const blob = new Blob([[headers.join(","), ...csvRows].join("\n")], { type: "text/csv;charset=utf-8;" });
         const url = window.URL.createObjectURL(blob);
         const a = document.createElement("a");
         a.href = url;
         a.download = `kaynin_talk_propositions_excel_${new Date().getTime()}.csv`;
         a.click();
         window.URL.revokeObjectURL(url);
       } else {
         const blob = new Blob([
           "Liste des questions proposées :\n\n" + 
           filteredProposedQuestions.map(c => `- Domaine: ${c.proposedDomain || 'Non précisé'}\n  Question: ${c.proposedQuestion || 'Aucune'}\n  Par: ${c.authorName} le ${new Date(c.createdAt).toLocaleDateString('fr-FR')}`).join("\n\n")
         ], { type: "application/msword" });
         const url = window.URL.createObjectURL(blob);
         const a = document.createElement("a");
         a.href = url;
         a.download = `kaynin_talk_propositions_word_${new Date().getTime()}.doc`;
         a.click();
         window.URL.revokeObjectURL(url);
       }
       toast({ title: "Export réussi" });
    }, 1000);
  };

  useEffect(() => {
    fetchDebates();
    fetchContributions();
    setLayout(getCmsLayout("home"));
  }, [fetchDebates, fetchContributions]);

  useEffect(() => {
    if (debates.length > 0 && !activeDebateId) {
      setActiveDebateId(debates[0].id);
    }
  }, [debates, activeDebateId]);

  useEffect(() => {
     if (activeDebateId) {
        const d = debates.find(d => d.id === activeDebateId);
        setDebConclusion(d?.adminConclusion || "");
     }
  }, [activeDebateId, debates]);

  const handleCreateDebate = () => {
    if (!newTitle) return;
    createDebate({
      title: newTitle,
      description: newDesc,
      status: "brouillon",
      titleAr: newTitleAr,
      descriptionAr: newDescAr,
      showStats: true
    });
    setIsAddOpen(false);
    setNewTitle("");
    setNewTitleAr("");
    setNewDesc("");
    setNewDescAr("");
    toast({ title: "Débat créé en brouillon" });
  };

  const handleAutoTranslate = () => {
    if (!newTitle && !newDesc) return;
    toast({ title: "Traduction en cours..." });
    // Mock auto-translate
    setTimeout(() => {
      const mockTranslations: Record<string, string> = {
        "Comment améliorer le transport public urbain ?": "كيف يمكن تحسين النقل العمومي الحضري؟",
        "Pour un échange participatif et inclusif,\nmerci de privilégier des contributions citoyennes, concises et ciblées": "من أجل نقاش تشاركي وشامل، يرجى إعطاء الأولوية لمساهمات مواطنة، موجزة وهادفة"
      };
      if (newTitle) setNewTitleAr(mockTranslations[newTitle] || `[ترجمة تلقائية]: ${newTitle}`);
      if (newDesc) setNewDescAr(mockTranslations[newDesc] || `[ترجمة تلقائية]: ${newDesc}`);
      toast({ title: "Traduction terminée" });
    }, 600);
  };

  const executeValidate = async (id: string, status: "valide" | "rejete") => {
    await adminValidateContribution(id, status === "valide");
    toast({
      title: status === "valide" ? "Contribution validée" : "Contribution rejetée",
      description: status === "valide" ? "Elle est maintenant visible publiquement." : "Elle est supprimée.",
      variant: status === "valide" ? "default" : "destructive"
    });
  };

  const handleUpdateStatus = async (id: string, status: TalkDebateStatus) => {
    await updateDebate(id, { status, publishedDate: status === 'publie' ? new Date().toISOString() : undefined });
    toast({ title: "Statut mis à jour", description: `Nouveau statut: ${status}` });
  };

  const handleToggleStats = async (id: string, showStats: boolean) => {
    await updateDebate(id, { showStats });
    toast({ title: "Préférences mises à jour" });
  };

  const handleSaveConclusionAndClose = async (id: string) => {
     await updateDebate(id, { adminConclusion: debConclusion, status: "cloture", publishedDate: activeDebate?.publishedDate || new Date().toISOString() });
     toast({ title: "Conclusion enregistrée avec succès", description: activeDebate?.status !== "cloture" ? "Le débat a été clôturé." : "" });
  };

  const handleSaveEdit = async (id: string) => {
    await adminEditContributionText(id, editText);
    setEditModeId(null);
    toast({ title: "Commentaire modifié avec succès" });
  };

  const handleEditDebateClick = (debate: TalkDebate) => {
    setEditDebateId(debate.id);
    setEditDebateTitle(debate.title);
    setEditDebateTitleAr(debate.titleAr || "");
    setEditDebateDesc(debate.description);
    setEditDebateDescAr(debate.descriptionAr || "");
    setIsEditDebateOpen(true);
  };

  const handleSaveDebateEdit = async () => {
    if (!editDebateId) return;
    await updateDebate(editDebateId, {
      title: editDebateTitle,
      titleAr: editDebateTitleAr,
      description: editDebateDesc,
      descriptionAr: editDebateDescAr
    });
    setIsEditDebateOpen(false);
    toast({ title: "Débat modifié", description: "Les modifications ont été enregistrées." });
  };

  const handleDeleteDebateClick = async (id: string) => {
    if (confirm("Êtes-vous sûr de vouloir supprimer cette question ?")) {
      await deleteDebate(id);
      toast({ title: "Débat supprimé", description: "Le débat a bien été supprimé." });
    }
  };

  const handleEditProposedClick = (cont: TalkContribution) => {
    setEditProposedId(cont.id);
    setEditProposedText(cont.proposedQuestion || "");
    setEditProposedDomain(cont.proposedDomain || "");
    setIsEditProposedOpen(true);
  };

  const handleSaveProposedEdit = async () => {
    if (!editProposedId) return;
    await adminUpdateContribution(editProposedId, {
      proposedQuestion: editProposedText,
      proposedDomain: editProposedDomain
    });
    setIsEditProposedOpen(false);
    toast({ title: "Question modifiée", description: "La proposition a été mise à jour." });
  };

  const handleDeleteProposedClick = async (id: string) => {
    if (confirm("Êtes-vous sûr de vouloir supprimer cette proposition ?")) {
      await deleteContribution(id);
      toast({ title: "Proposition supprimée" });
    }
  };

  const handleValidateProposedQuestion = async (cont: TalkContribution) => {
    await createDebate({
      title: cont.proposedQuestion || cont.text,
      description: `Sujet proposé par le citoyen ${cont.authorName}.`,
      titleAr: "",
      descriptionAr: "",
      status: "brouillon",
      showStats: true
    });
    await deleteContribution(cont.id);
    toast({ title: "Question validée", description: "Elle a été ajoutée aux questions en attente." });
  };

  // Synchronise le débat sélectionné avec l'onglet actif
  useEffect(() => {
    if (activeMainTab === "en_ligne") {
      const online = debates.find(d => d.status === "publie");
      if (online) {
        setActiveDebateId(online.id);
      }
    } else if (activeMainTab === "cloturees") {
      const closed = debates.filter(d => d.status === "cloture");
      if (closed.length > 0 && !closed.some(d => d.id === activeDebateId)) {
        setActiveDebateId(closed[0].id);
      }
    }
  }, [activeMainTab, debates]);

  const filteredContributions = contributions.filter(c => c.debateId === activeDebateId).sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const activeDebate = debates.find(d => d.id === activeDebateId);
  const onlineDebate = debates.find(d => d.status === "publie");
  const pendingDebates = debates.filter(d => d.status === "brouillon" || d.status === "planifie");
  const existingReport = activeDebateId ? reports.find(r => r.debateId === activeDebateId) : undefined;
  const [showReport, setShowReport] = useState<boolean>(false);

  const validsForStats = filteredContributions.filter(c => c.status === "valide");
  const realStats = {
     total: validsForStats.length,
     hommes: validsForStats.filter(c => c.genre === "homme").length,
     femmes: validsForStats.filter(c => c.genre === "femme").length,
     ong: validsForStats.filter(c => c.authorCategory === "ONG").length,
     citoyens: validsForStats.filter(c => c.authorCategory === "CITOYEN").length,
     partis: validsForStats.filter(c => c.authorCategory === "PARTI").length,
     institutions: validsForStats.filter(c => c.authorCategory === "INSTITUTION").length,
     likes: validsForStats.reduce((acc, c) => acc + c.likes, 0)
  };

  // Filtrage et groupement par Year/Month/Q
  const filteredDebatesList = useMemo(() => {
     let list = debates;
     if (filterYear !== "all") {
        list = list.filter(d => new Date(d.createdAt).getFullYear().toString() === filterYear);
     }
     if (filterMonth !== "all") {
        list = list.filter(d => new Date(d.createdAt).getMonth().toString() === filterMonth);
     }
     if (filterQ !== "all") {
        list = list.filter(d => getQuestionOfMonth(new Date(d.createdAt)).toString() === filterQ);
     }
     if (searchQuery.trim() !== "") {
        const q = searchQuery.toLowerCase();
        list = list.filter(d => d.title.toLowerCase().includes(q));
     }
     
     if (activeMainTab === "en_ligne") {
         list = list.filter(d => d.status === "publie");
     } else if (activeMainTab === "en_attente") {
         list = list.filter(d => d.status === "brouillon" || d.status === "planifie");
     } else if (activeMainTab === "cloturees") {
         list = list.filter(d => d.status === "cloture");
     }

     return list.reduce((acc, deb) => {
        const d = new Date(deb.createdAt);
        const yStr = d.getFullYear();
        const mStr = d.toLocaleDateString('fr-FR', { month: 'long' });
        const qNum = getQuestionOfMonth(d);
        const qStr = `${qNum}${qNum === 1 ? 'ère' : 'ème'} Q`;
        
        const key = `${yStr} / ${mStr.charAt(0).toUpperCase() + mStr.slice(1)} / ${qStr}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(deb);
        return acc;
     }, {} as Record<string, TalkDebate[]>);
  }, [debates, filterYear, filterMonth, filterQ, searchQuery, activeMainTab]);

  const filteredClosedDebatesList = useMemo(() => {
     let list = debates.filter(d => d.status === "cloture");
     if (filterYear !== "all") {
        list = list.filter(d => new Date(d.createdAt).getFullYear().toString() === filterYear);
     }
     if (filterMonth !== "all") {
        list = list.filter(d => new Date(d.createdAt).getMonth().toString() === filterMonth);
     }
     if (filterQ !== "all") {
        list = list.filter(d => getQuestionOfMonth(new Date(d.createdAt)).toString() === filterQ);
     }
     if (searchQuery.trim() !== "") {
        const q = searchQuery.toLowerCase();
        list = list.filter(d => d.title.toLowerCase().includes(q));
     }

     return list.reduce((acc, deb) => {
        const d = new Date(deb.createdAt);
        const yStr = d.getFullYear();
        const mStr = d.toLocaleDateString('fr-FR', { month: 'long' });
        const qNum = getQuestionOfMonth(d);
        const qStr = `${qNum}${qNum === 1 ? 'ère' : 'ème'} Q`;
        
        const key = `${yStr} / ${mStr.charAt(0).toUpperCase() + mStr.slice(1)} / ${qStr}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(deb);
        return acc;
     }, {} as Record<string, TalkDebate[]>);
  }, [debates, filterYear, filterMonth, filterQ, searchQuery]);

  const triggerExport = (format: "excel" | "pdf" | "word") => {
    setExportFormat(format);
    setIsExportOpen(true);
  };

  const executeExport = async () => {
    setIsExportOpen(false);
    toast({ title: `Export ${exportFormat.toUpperCase()}`, description: "Génération en cours..." });
    
    let chartsImage;
    if (includeStats) {
       const chartsEl = document.getElementById("stats-charts-container");
       if (chartsEl) {
          const canvas = await html2canvas(chartsEl, { scale: 2, backgroundColor: "#ffffff" });
          chartsImage = canvas.toDataURL("image/png");
       }
    }

    exportDebateAnalysisReport(
      activeDebate,
      filteredContributions,
      existingReport,
      exportFormat,
      includeStats,
      includeWatermark,
      chartsImage
    );
  };

  const generateAIReport = () => {
    toast({ title: "Génération en cours...", description: "L'IA analyse les contributions..." });
    const valids = filteredContributions.filter(c => c.status === "valide");
    setTimeout(() => {
      const topProp = valids.sort((a,b) => b.likes - a.likes)[0];
      const newReport: TalkReport = {
        id: `report_${Date.now()}`,
        debateId: activeDebateId,
        generatedAt: new Date().toISOString(),
        sujet_debat: activeDebate?.title || "Sujet Inconnu",
        synthese_globale: "Le débat a suscité un intérêt modéré avec une diversité d'intervenants.",
        statistiques_participation: {
          total_contributions: valids.length,
          repartition_genre: {
            hommes: valids.filter(c => c.genre === "homme").length,
            femmes: valids.filter(c => c.genre === "femme").length,
            non_precise: valids.filter(c => !c.genre || c.genre === "non_precise").length
          },
          repartition_acteurs: {
            citoyens: valids.filter(c => c.authorCategory === "CITOYEN").length,
            ong: valids.filter(c => c.authorCategory === "ONG").length,
            partis_politiques: valids.filter(c => c.authorCategory === "PARTI").length,
            institutions: valids.filter(c => c.authorCategory === "INSTITUTION").length
          }
        },
        proposition_phare: {
          titre: "Proposition Citoyenne Majeure",
          auteur: topProp ? topProp.authorName : "N/A",
          description: topProp ? topProp.text : "Aucune proposition",
          nombre_likes: topProp ? topProp.likes : 0
        },
        analyse_par_categorie: {
          citoyens: { tendances_principales: ["Demande de numérisation"], citations_marquantes: [] },
          ong: { tendances_principales: ["Focus sur l'écologie"], citations_marquantes: [] },
          partis_politiques: { tendances_principales: ["Financement public"], citations_marquantes: [] },
          institutions: { tendances_principales: ["Conformité réglementaire"], citations_marquantes: [] }
        },
        recommandations_actionnables: [
        ]
      };
      
      saveReport(newReport);
      setShowReport(true);
      toast({ title: "Rapport généré avec succès !" });
    }, 1500);
  };

  const talkSection = layout.find(s => s.id === "talk");
  const isTalkVisible = talkSection?.visible ?? false;

  const toggleVisibility = () => {
     const newLayout = [...layout];
     const idx = newLayout.findIndex(s => s.id === "talk");
     if (idx >= 0) {
        newLayout[idx] = { ...newLayout[idx], visible: !newLayout[idx].visible };
        setLayout(newLayout);
        publishCmsLayout("home", newLayout);
        toast({ title: newLayout[idx].visible ? "Section activée sur l'accueil" : "Section désactivée de l'accueil" });
     }
  };

  const handlePlacementChange = (placement: string) => {
     const newLayout = [...layout];
     const talkIdx = newLayout.findIndex(s => s.id === "talk");
     if (talkIdx < 0) return;
     const talkItem = newLayout.splice(talkIdx, 1)[0];

     if (placement === "above_agenda") {
        const agendaIdx = newLayout.findIndex(s => s.id === "parties-action");
        if (agendaIdx >= 0) newLayout.splice(agendaIdx, 0, talkItem);
        else newLayout.splice(3, 0, talkItem);
     } else if (placement === "top") {
        newLayout.splice(1, 0, talkItem); // Fix under hero
     } else if (placement === "bottom") {
        const instIdx = newLayout.findIndex(s => s.id === "cta");
        if (instIdx >= 0) newLayout.splice(instIdx, 0, talkItem);
        else newLayout.push(talkItem);
     }

     setLayout(newLayout);
     publishCmsLayout("home", newLayout);
     toast({ title: "Emplacement mis à jour" });
  };

  const currentPlacement = useMemo(() => {
     if (!talkSection) return "above_agenda";
     const talkIdx = layout.findIndex(s => s.id === "talk");
     const agendaIdx = layout.findIndex(s => s.id === "parties-action");
     const accIdx = layout.findIndex(s => s.id === "accreditation");
     const ctaIdx = layout.findIndex(s => s.id === "cta");
     
     if (talkIdx === 1) return "top";
     if (talkIdx >= (ctaIdx !== -1 ? ctaIdx - 1 : layout.length - 2)) return "bottom";
     
     return "above_agenda";
  }, [layout, talkSection]);

  return (
    <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="w-full space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-3">
        <div>
          <h1 className="text-xl md:text-3xl font-display font-bold text-foreground">Kayn'in Talk (Administration)</h1>
          <p className="text-muted-foreground font-body mt-1 text-sm">Gérez les questions, clôturez les débats avec une note de synthèse et gérez la modération.</p>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant={likesEnabled ? "default" : "outline"}
            onClick={toggleLikes}
            className={likesEnabled ? "bg-indigo-600 hover:bg-indigo-700" : "text-muted-foreground border-border"}
          >
            {likesEnabled ? "Désactiver les likes" : "Activer les likes"}
          </Button>
          <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2 bg-pink-600 hover:bg-pink-700">
                <Plus className="w-4 h-4" /> Ajouter une question
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nouvelle Question de la semaine</DialogTitle>
                <DialogDescription>Cette question sera enregistrée en tant que brouillon.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4 max-h-[60vh] overflow-y-auto custom-scrollbar px-1">
                <div className="space-y-2">
                  <Label>Titre (La question) *</Label>
                  <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Ex: Faut-il encourager le télétravail ?" />
                </div>
                <div className="space-y-2">
                  <Label>Description / Contexte *</Label>
                  <Textarea value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="Donnez plus de contexte sur cette thématique..." />
                </div>
                
                <div className="pt-2 pb-1 border-t flex justify-end">
                  <Button type="button" variant="outline" size="sm" onClick={handleAutoTranslate} className="text-indigo-600 gap-2">
                    Traduire automatiquement en arabe
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>Titre (Arabe) - السؤال</Label>
                  <Input dir="rtl" value={newTitleAr} onChange={e => setNewTitleAr(e.target.value)} placeholder="السؤال باللغة العربية..." />
                </div>
                <div className="space-y-2">
                  <Label>Description (Arabe) - الوصف</Label>
                  <Textarea dir="rtl" value={newDescAr} onChange={e => setNewDescAr(e.target.value)} placeholder="وصف النقاش باللغة العربية..." />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCreateDebate} className="w-full mt-4">Créer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <TabsList className="mb-4 bg-muted/50 w-full justify-start h-auto p-1 flex-wrap">
        <TabsTrigger value="en_ligne" className="rounded-lg font-semibold py-2">Débat (En ligne)</TabsTrigger>
        <TabsTrigger value="en_attente" className="rounded-lg font-semibold py-2">Questions en attente</TabsTrigger>
        <TabsTrigger value="cloturees" className="rounded-lg font-semibold py-2">Clôturées & archivées</TabsTrigger>
        <TabsTrigger value="proposed" className="rounded-lg font-semibold py-2">Questions proposées ({proposedQuestions.length})</TabsTrigger>
        <TabsTrigger value="stats" className="rounded-lg font-semibold py-2">Statistiques Globales</TabsTrigger>
      </TabsList>

      {/* -------------------- TAB 1: EN LIGNE -------------------- */}
      <TabsContent value="en_ligne" className="space-y-6">
        {/* Configuration Widget Accueil */}
        <Card className="border-indigo-100 bg-indigo-50/50 shadow-sm">
          <CardContent className="p-4 flex flex-col lg:flex-row items-center justify-between gap-4">
             <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex flex-col items-center justify-center text-indigo-600">
                   <Settings className="w-5 h-5" />
                </div>
                <div>
                   <h3 className="font-bold text-foreground">Affichage public sur l'accueil</h3>
                   <p className="text-xs text-muted-foreground">Contrôlez la visibilité et l'emplacement de la section Kayn'in Talk pour les visiteurs.</p>
                </div>
             </div>
             
             <div className="flex items-center flex-wrap gap-4">
                <div className="flex items-center gap-2">
                   <Label className="text-xs font-semibold whitespace-nowrap text-muted-foreground">Visibilité :</Label>
                   <Button 
                      variant={isTalkVisible ? "default" : "outline"} 
                      size="sm" 
                      onClick={toggleVisibility}
                      className={isTalkVisible ? "bg-emerald-600 hover:bg-emerald-700 w-32 border-none" : "w-32 border-red-200 text-red-600 bg-red-50 hover:bg-red-100"}
                   >
                      {isTalkVisible ? <><Eye className="w-4 h-4 mr-2"/> En ligne</> : <><EyeOff className="w-4 h-4 mr-2"/> Masqué</>}
                   </Button>
                </div>
                <div className="flex items-center gap-2">
                   <Label className="text-xs font-semibold whitespace-nowrap text-muted-foreground">Position :</Label>
                   <Select value={currentPlacement} onValueChange={handlePlacementChange}>
                      <SelectTrigger className="w-[280px] h-9 text-xs bg-white">
                         <SelectValue placeholder="Choisir un emplacement" />
                      </SelectTrigger>
                      <SelectContent>
                         <SelectItem value="top">Haut de page (Immédiat)</SelectItem>
                         <SelectItem value="above_agenda">Au-dessus de l'Agenda National</SelectItem>
                         <SelectItem value="bottom">En pied de page (Bas)</SelectItem>
                      </SelectContent>
                   </Select>
                </div>
             </div>
          </CardContent>
        </Card>

        {onlineDebate ? (
          <div className="space-y-6">
            <Card className="border-border shadow-sm bg-gradient-to-br from-card to-muted/20">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row justify-between md:items-start gap-4">
                  <div className="space-y-2">
                    <h2 className="text-2xl font-display font-bold text-foreground">
                       {onlineDebate.title}
                    </h2>
                    {onlineDebate.titleAr && (
                       <h3 className="text-lg font-display text-indigo-600 block flex-row-reverse text-right mt-1" dir="rtl">{onlineDebate.titleAr}</h3>
                    )}
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{onlineDebate.description}</p>
                    {onlineDebate.descriptionAr && (
                       <p className="text-sm text-indigo-600 opacity-90 whitespace-pre-wrap block flex-row-reverse text-right mt-2" dir="rtl">{onlineDebate.descriptionAr}</p>
                    )}
                    <div className="flex gap-2 items-center flex-wrap mt-2">
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                         EN LIGNE
                      </Badge>
                      <span className="text-xs text-muted-foreground">Publié le {onlineDebate.publishedDate ? new Date(onlineDebate.publishedDate).toLocaleDateString() : new Date(onlineDebate.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleUpdateStatus(onlineDebate.id, "brouillon")}
                      className="gap-2 border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <RotateCcw className="w-4 h-4"/> Réinitialiser (Mettre hors ligne)
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleToggleStats(onlineDebate.id, !onlineDebate.showStats)} className="gap-2">
                      {onlineDebate.showStats ? <><EyeOff className="w-4 h-4"/> Masquer Stats Publiques</> : <><Eye className="w-4 h-4"/> Afficher Stats Publiques</>}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Modération pour le débat en ligne */}
            <Card className="border-border shadow-sm">
              <CardHeader className="flex flex-row justify-between items-center bg-muted/20 border-b pb-4">
                <div>
                  <CardTitle className="text-lg font-display">Modération des avis</CardTitle>
                  <CardDescription>Affichage des commentaires en bulle (Maximum 250 caractères).</CardDescription>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => triggerExport('pdf')} className="text-red-600 gap-1"><FileText className="w-3.5 h-3.5"/> PDF</Button>
                  <Button size="sm" variant="outline" onClick={() => triggerExport('word')} className="text-blue-600 gap-1"><File className="w-3.5 h-3.5"/> Word</Button>
                  <Button size="sm" variant="outline" onClick={() => triggerExport('excel')} className="text-green-600 gap-1"><FileSpreadsheet className="w-3.5 h-3.5"/> Excel</Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="flex gap-2 mb-4 p-2 mx-4 mt-4 border border-indigo-100 rounded-xl bg-indigo-50/50 dark:bg-indigo-900/10 dark:border-indigo-800/50 items-center shadow-sm w-max">
                  <span className="text-xs font-semibold text-muted-foreground mr-2">Mise en forme globale :</span>
                  <button onClick={() => setGlobalFormat(globalFormat.includes('font-bold') ? globalFormat.replace('font-bold', 'font-normal') : globalFormat.replace('font-normal', 'font-bold'))} className={`p-1.5 rounded-sm hover:bg-muted ${globalFormat.includes('font-bold') ? 'bg-white dark:bg-slate-800 shadow-sm' : ''}`}><Bold className="w-4 h-4" /></button>
                  <button onClick={() => setGlobalFormat(globalFormat.includes('italic') ? globalFormat.replace('italic', '') : globalFormat + ' italic')} className={`p-1.5 rounded-sm hover:bg-muted ${globalFormat.includes('italic') ? 'bg-white dark:bg-slate-800 shadow-sm' : ''}`}><Italic className="w-4 h-4" /></button>
                  <div className="w-px h-5 bg-border mx-2"></div>
                  <button onClick={() => setGlobalFormat(globalFormat.replace(/text-[a-z0-9-]+/g, '').trim() + ' text-foreground')} className={`w-6 h-6 rounded-full bg-foreground border mx-0.5 ${globalFormat.includes('text-foreground') ? 'ring-2 ring-primary ring-offset-2' : ''}`} title="Noir/Normal"></button>
                  <button onClick={() => setGlobalFormat(globalFormat.replace(/text-[a-z0-9-]+/g, '').trim() + ' text-slate-500')} className={`w-6 h-6 rounded-full bg-slate-500 border mx-0.5 ${globalFormat.includes('text-slate-500') ? 'ring-2 ring-primary ring-offset-2' : ''}`} title="Gris"></button>
                  <button onClick={() => setGlobalFormat(globalFormat.replace(/text-[a-z0-9-]+/g, '').trim() + ' text-blue-600')} className={`w-6 h-6 rounded-full bg-blue-600 border mx-0.5 ${globalFormat.includes('text-blue-600') ? 'ring-2 ring-primary ring-offset-2' : ''}`} title="Bleu"></button>
                </div>
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="w-full text-sm font-body">
                    <thead className="bg-muted/30 border-b border-border/50 text-muted-foreground">
                      <tr>
                        <th className="py-4 px-6 text-left">Auteur & Infos</th>
                        <th className="py-4 px-4 text-left">Commentaire</th>
                        <th className="py-4 px-4 text-center">Likes</th>
                        <th className="py-4 px-4 text-center">Statut</th>
                        <th className="py-4 px-6 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/30">
                      {contributions.filter(c => c.debateId === onlineDebate.id).sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map((cont) => (
                        <tr key={cont.id} className="hover:bg-muted/10 transition">
                          <td className="py-4 px-6 align-top">
                            <div className="font-bold text-foreground flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cont.authorColor }}></div>
                              <span className="truncate max-w-[120px]">{cont.authorName}</span>
                            </div>
                            <div className="text-xs text-muted-foreground capitalize mt-1 flex gap-1 flex-wrap">
                              <span className="border px-1 py-0.5 rounded-md">{cont.authorCategory}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 align-top">
                            {editModeId === cont.id ? (
                               <div className="flex flex-col gap-2 relative z-10 w-full min-w-[250px]">
                                  <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} className="min-h-[100px] text-sm custom-scrollbar" />
                                  <div className="flex gap-2 justify-end mt-1">
                                     <Button size="sm" variant="outline" onClick={() => setEditModeId(null)}>Annuler</Button>
                                     <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => handleSaveEdit(cont.id)}>Enregistrer</Button>
                                  </div>
                               </div>
                            ) : (
                               <div className={`relative inline-block max-w-[50ch] bg-indigo-50 border border-indigo-100 dark:bg-indigo-900/30 dark:border-indigo-800 rounded-2xl rounded-tl-sm p-3 shadow-sm text-sm leading-relaxed whitespace-pre-wrap break-words ${globalFormat}`}>
                                  "{cont.text}"
                                </div>
                            )}
                          </td>
                          <td className="py-4 px-4 text-center font-bold text-indigo-600 align-top">{cont.likes}</td>
                          <td className="py-4 px-4 text-center align-top">
                            {cont.status === 'en_attente' && <Badge variant="outline" className="text-orange-600 bg-orange-50">En attente</Badge>}
                            {cont.status === 'valide' && <Badge variant="outline" className="text-emerald-600 bg-emerald-50">Validé</Badge>}
                            {cont.status === 'rejete' && <Badge variant="outline" className="text-destructive bg-destructive/10">Rejeté</Badge>}
                          </td>
                          <td className="py-4 px-6 text-right align-top">
                            <div className="flex justify-end gap-2 text-muted-foreground">
                              {editModeId !== cont.id && (
                                 <Button size="icon" variant="ghost" onClick={() => { setEditModeId(cont.id); setEditText(cont.text); }} className="hover:text-indigo-600" title="Éditer">
                                    <Pencil className="w-4 h-4" />
                                 </Button>
                              )}
                              {cont.status === 'en_attente' ? (
                                <>
                                  <Button size="icon" variant="outline" className="text-destructive border-transparent hover:bg-destructive/10" onClick={() => executeValidate(cont.id, 'rejete')} title="Rejeter">
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                  <Button size="icon" className="bg-emerald-600 hover:bg-emerald-700 border-none text-white" onClick={() => executeValidate(cont.id, 'valide')} title="Valider">
                                    <CheckCircle2 className="w-4 h-4" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button size="sm" variant="ghost" onClick={() => executeValidate(cont.id, cont.status === 'valide' ? 'rejete' : 'valide')} className="text-muted-foreground">
                                    Basculer
                                  </Button>
                                  <Button size="icon" variant="ghost" onClick={async () => { if(confirm("Êtes-vous sûr de vouloir supprimer ce commentaire ?")) { await deleteContribution(cont.id); toast({title:"Commentaire supprimé"}); } }} className="text-destructive hover:bg-destructive/10 hover:text-destructive" title="Supprimer">
                                    <Trash className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {contributions.filter(c => c.debateId === onlineDebate.id).length === 0 && (
                        <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">Aucun commentaire pour cette question.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="border-dashed border-2 border-indigo-200 bg-indigo-50/20 p-12 text-center rounded-3xl">
            <CardContent className="space-y-4">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto text-indigo-600">
                <EyeOff className="w-8 h-8"/>
              </div>
              <h3 className="text-xl font-bold text-foreground">Aucun débat n'est actuellement en ligne</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">Pour publier une question et la rendre visible aux citoyens, rendez-vous dans l'onglet <strong>"Questions en attente"</strong> et cliquez sur "Publier" sur la question de votre choix.</p>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      {/* -------------------- TAB 2: EN ATTENTE -------------------- */}
      <TabsContent value="en_attente" className="space-y-6">
        <Card className="border-border shadow-sm">
          <CardHeader className="flex flex-row justify-between items-center bg-muted/20 border-b pb-4 mb-4">
            <div>
              <CardTitle className="text-lg font-display">Base des questions en attente</CardTitle>
              <CardDescription>Questions enregistrées en brouillon ou planifiées, prêtes à être publiées en ligne.</CardDescription>
            </div>
            <Button size="sm" onClick={() => setIsAddOpen(true)} className="gap-2 bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4" /> Ajouter une question
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-sm font-body">
                <thead className="bg-muted/30 border-b border-border/50 text-muted-foreground">
                  <tr>
                    <th className="py-4 px-6 text-left">Question (Français)</th>
                    <th className="py-4 px-6 text-left">Question (Arabe)</th>
                    <th className="py-4 px-4 text-center">Statut</th>
                    <th className="py-4 px-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/30">
                  {pendingDebates.length > 0 ? pendingDebates.map((d) => (
                    <tr key={d.id} className="hover:bg-muted/10 transition">
                      <td className="py-4 px-6 align-top max-w-[300px]">
                        <div className="font-bold text-foreground break-words">{d.title}</div>
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{d.description}</div>
                      </td>
                      <td className="py-4 px-6 align-top max-w-[300px] text-right" dir="rtl">
                        <div className="font-bold text-indigo-600 break-words">{d.titleAr || <span className="text-muted-foreground font-normal italic text-xs">Non traduit</span>}</div>
                        <div className="text-xs text-slate-500 mt-1 line-clamp-2">{d.descriptionAr}</div>
                      </td>
                      <td className="py-4 px-4 text-center align-top whitespace-nowrap">
                        <Badge variant="outline" className={d.status === 'planifie' ? 'text-orange-600 bg-orange-50' : 'text-slate-600 bg-slate-50'}>
                          {d.status === 'planifie' ? 'Planifié' : 'Brouillon'}
                        </Badge>
                      </td>
                      <td className="py-4 px-6 text-right align-top whitespace-nowrap">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 font-bold text-white border-none" onClick={() => handleUpdateStatus(d.id, "publie")}>
                            <Eye className="w-4 h-4 mr-1"/> Publier
                          </Button>
                          <Button size="icon" variant="outline" onClick={() => handleEditDebateClick(d)} title="Modifier la question">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => handleDeleteDebateClick(d.id)} title="Supprimer la question">
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={4} className="py-12 text-center text-muted-foreground border border-dashed rounded-3xl m-4">
                        Aucune question en attente. Cliquez sur "Ajouter une question" pour commencer.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* -------------------- TAB 3: CLOTUREES -------------------- */}
      <TabsContent value="cloturees" className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Sidebar Liste des questions clôturées */}
          <div className="col-span-1 border rounded-xl bg-card shadow-sm space-y-0 flex flex-col h-[700px]">
            <div className="p-4 border-b">
               <h2 className="font-display font-bold flex items-center gap-2 mb-4"><Archive className="w-4 h-4" /> Questions Précédentes</h2>
               <div className="flex flex-col gap-2 mb-3">
                  <Select value={filterYear} onValueChange={setFilterYear}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Année" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les années</SelectItem>
                      <SelectItem value="2026">2026</SelectItem>
                      <SelectItem value="2025">2025</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={filterMonth} onValueChange={setFilterMonth}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Mois" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        {Array.from({length: 12}).map((_, i) => (
                          <SelectItem key={i} value={i.toString()}>
                            {new Date(0, i).toLocaleDateString('fr-FR', { month: 'long' })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={filterQ} onValueChange={setFilterQ}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Période" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes</SelectItem>
                        <SelectItem value="1">1ère Q</SelectItem>
                        <SelectItem value="2">2ème Q</SelectItem>
                        <SelectItem value="3">3ème Q</SelectItem>
                        <SelectItem value="4">4ème Q</SelectItem>
                        <SelectItem value="5">5ème Q</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
               </div>
               <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
                  <Input 
                     placeholder="Recherche mots-clés..." 
                     className="pl-9 h-9 text-xs" 
                     value={searchQuery}
                     onChange={(e) => setSearchQuery(e.target.value)}
                  />
               </div>
            </div>
            <div className="p-4 space-y-6 overflow-y-auto custom-scrollbar flex-1">
              {Object.keys(filteredClosedDebatesList).sort().reverse().map(period => (
                <div key={period} className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{period}</p>
                  <div className="space-y-1">
                    {filteredClosedDebatesList[period].map(d => (
                      <button
                        key={d.id}
                        onClick={() => setActiveDebateId(d.id)}
                        className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold transition-all border ${activeDebateId === d.id ? "bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-900/30" : "bg-transparent border-transparent hover:bg-muted text-foreground"}`}
                      >
                        <div className="truncate">{d.title}</div>
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-[10px] text-muted-foreground">{contributions.filter(c => c.debateId === d.id).length} contributions</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {Object.keys(filteredClosedDebatesList).length === 0 && (
                 <p className="text-xs text-center text-muted-foreground italic">Aucune question clôturée trouvée.</p>
              )}
            </div>
          </div>

          {/* Détail du débat clôturé sélectionné */}
          <div className="col-span-1 md:col-span-3 space-y-4">
            {activeDebate && activeDebate.status === 'cloture' ? (
              <div className="space-y-6">
                <Card className="border-border shadow-sm bg-gradient-to-br from-card to-muted/20">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row justify-between md:items-start gap-4">
                      <div className="space-y-2">
                        <h2 className="text-2xl font-display font-bold text-foreground">
                           {activeDebate.title}
                        </h2>
                        {activeDebate.titleAr && (
                           <h3 className="text-lg font-display text-indigo-600 block flex-row-reverse text-right mt-1" dir="rtl">{activeDebate.titleAr}</h3>
                        )}
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{activeDebate.description}</p>
                        {activeDebate.descriptionAr && (
                           <p className="text-sm text-indigo-600 opacity-90 whitespace-pre-wrap block flex-row-reverse text-right mt-2" dir="rtl">{activeDebate.descriptionAr}</p>
                        )}
                        <div className="flex gap-2 items-center flex-wrap mt-2">
                          <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                             ARCHIVÉE (QUESTION PRÉCÉDENTE)
                          </Badge>
                          <span className="text-xs text-muted-foreground">Clôturé le {activeDebate.closedAt ? new Date(activeDebate.closedAt).toLocaleDateString() : new Date().toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Tabs defaultValue="report" className="w-full">
                  <div className="flex flex-col md:flex-row justify-between md:items-center gap-3 mb-6">
                    <TabsList className="bg-muted/50 p-1">
                      <TabsTrigger value="report" className="rounded-lg font-semibold flex items-center gap-2">
                        <FileJson className="w-4 h-4" /> Rapport & Conclusion
                      </TabsTrigger>
                      <TabsTrigger value="comments" className="rounded-lg font-semibold flex items-center gap-2">
                        <MessageCircle className="w-4 h-4" /> Avis archivés ({filteredContributions.length})
                      </TabsTrigger>
                    </TabsList>
                    
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => triggerExport('pdf')} className="text-red-600 gap-1"><FileText className="w-3.5 h-3.5"/> PDF</Button>
                      <Button size="sm" variant="outline" onClick={() => triggerExport('word')} className="text-blue-600 gap-1"><File className="w-3.5 h-3.5"/> Word</Button>
                      <Button size="sm" variant="outline" onClick={() => triggerExport('excel')} className="text-green-600 gap-1"><FileSpreadsheet className="w-3.5 h-3.5"/> Excel</Button>
                    </div>
                  </div>

                  <TabsContent value="report" className="mt-0">
                    <Card className="border-border shadow-sm">
                      <CardHeader>
                        <CardTitle className="text-xl font-display flex gap-2 items-center">
                            <Archive className="w-5 h-5 text-indigo-500" /> Statistiques & Synthèse
                        </CardTitle>
                        <CardDescription>Rapport synthétique et conclusion administrative de la question précédente.</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Stats Section */}
                          <div className="space-y-4">
                            <h3 className="font-bold text-lg text-foreground border-b pb-2">1. Aperçu Réel des Statistiques</h3>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div className="bg-slate-900 rounded-xl p-4 border border-slate-800 text-center shadow-sm">
                                <p className="text-slate-400 text-xs font-semibold mb-1 uppercase tracking-wider">Participants</p>
                                <p className="text-emerald-400 text-3xl font-display font-bold">{realStats.total}</p>
                              </div>
                              <div className="bg-slate-900 rounded-xl p-4 border border-slate-800 text-center shadow-sm">
                                <p className="text-slate-400 text-xs font-semibold mb-1 uppercase tracking-wider">Interactions</p>
                                <p className="text-emerald-400 text-3xl font-display font-bold">{realStats.likes} <span className="text-xs text-slate-500 font-normal">Likes</span></p>
                              </div>
                            </div>

                            <div id="stats-charts-container" className="bg-white dark:bg-slate-950 p-5 rounded-xl border shadow-sm grid grid-cols-1 gap-6">
                              <div className="h-[250px]">
                                <h4 className="text-sm font-bold text-center mb-2">Répartition Hommes / Femmes</h4>
                                <ResponsiveContainer width="100%" height="100%">
                                  <PieChart>
                                    <Pie data={[{ name: 'Hommes', value: realStats.hommes }, { name: 'Femmes', value: realStats.femmes }]} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                                      <Cell fill="#3b82f6" />
                                      <Cell fill="#ec4899" />
                                    </Pie>
                                    <RechartsTooltip />
                                    <Legend />
                                  </PieChart>
                                </ResponsiveContainer>
                              </div>
                              <div className="h-[250px] border-t pt-4">
                                <h4 className="text-sm font-bold text-center mb-2">Catégories d'Acteurs</h4>
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart data={[
                                    { name: 'Citoyens', count: realStats.citoyens },
                                    { name: 'ONG', count: realStats.ong },
                                    { name: 'Partis', count: realStats.partis },
                                    { name: 'Institutions', count: realStats.institutions }
                                  ]} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" />
                                    <YAxis dataKey="name" type="category" width={80} fontSize={12} />
                                    <RechartsTooltip />
                                    <Bar dataKey="count" fill="#10b981" radius={[0, 4, 4, 0]} barSize={20} />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                          </div>

                          {/* Conclusion Section */}
                          <div className="space-y-4 flex flex-col">
                            <h3 className="font-bold text-lg text-foreground border-b pb-2">2. Commentaire Admin & Conclusion</h3>
                            <div className="space-y-2 flex-1 flex flex-col">
                              <Label className="text-slate-500">Synthèse finale qui sera visible en bas de la page Kaynin Talk.</Label>
                              <Textarea 
                                value={debConclusion} 
                                onChange={e => setDebConclusion(e.target.value)} 
                                placeholder="Ex: Merci pour l'ensemble de vos retours constructifs. Grâce à vos 45 propositions validées, la Commission examinera l'option d'une application unifiée." 
                                className="min-h-[150px] flex-1 border-indigo-200 focus-visible:ring-indigo-500 resize-none text-sm"
                              />
                            </div>
                            
                            <div className="flex flex-col gap-2">
                              <div className="w-full p-3 border border-emerald-200 bg-emerald-50 rounded-xl text-emerald-700 flex items-center gap-2 justify-center font-bold text-sm">
                                <CheckCircle2 className="w-4 h-4"/> Débat archivé
                              </div>
                              <Button 
                                onClick={() => handleSaveConclusionAndClose(activeDebate.id)}
                                variant="outline"
                                className="w-full border-indigo-200 text-indigo-700 hover:bg-indigo-50 font-bold"
                              >
                                <Pencil className="w-4 h-4 mr-2"/>
                                Enregistrer / Modifier la Conclusion
                              </Button>
                            </div>

                            {/* Options d'affichage des statistiques pour un débat clôturé */}
                            <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                              <Label className="font-bold text-foreground">3. Statistiques affichées sur cette question précédente</Label>
                              <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                  <Checkbox 
                                    id="showStats_participants" 
                                    checked={activeDebate.showStats_participants !== false} 
                                    onCheckedChange={(c) => { updateDebate(activeDebate.id, { showStats_participants: !!c }); toast({ title: "Préférences mises à jour" }); }} 
                                  />
                                  <label htmlFor="showStats_participants" className="text-sm text-slate-700 cursor-pointer">Afficher le nombre de participants</label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Checkbox 
                                    id="showStats_gender" 
                                    checked={activeDebate.showStats_gender !== false} 
                                    onCheckedChange={(c) => { updateDebate(activeDebate.id, { showStats_gender: !!c }); toast({ title: "Préférences mises à jour" }); }} 
                                  />
                                  <label htmlFor="showStats_gender" className="text-sm text-slate-700 cursor-pointer">Afficher la répartition Hommes/Femmes</label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Checkbox 
                                    id="showStats_likes" 
                                    checked={activeDebate.showStats_likes !== false} 
                                    onCheckedChange={(c) => { updateDebate(activeDebate.id, { showStats_likes: !!c }); toast({ title: "Préférences mises à jour" }); }} 
                                  />
                                  <label htmlFor="showStats_likes" className="text-sm text-slate-700 cursor-pointer">Afficher le nombre de likes cumulés</label>
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground italic">Ces statistiques s'afficheront publiquement dans la bulle "Questions précédentes" si elles sont activées.</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="comments" className="mt-0">
                    <Card className="border-border shadow-sm">
                      <CardHeader>
                        <CardTitle className="text-lg font-display">Avis archivés</CardTitle>
                        <CardDescription>Tous les commentaires approuvés lors de ce débat.</CardDescription>
                      </CardHeader>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto custom-scrollbar">
                          <table className="w-full text-sm font-body">
                            <thead className="bg-muted/30 border-b border-border/50 text-muted-foreground">
                              <tr>
                                <th className="py-4 px-6 text-left">Auteur & Infos</th>
                                <th className="py-4 px-4 text-left">Commentaire</th>
                                <th className="py-4 px-4 text-center">Likes</th>
                                <th className="py-4 px-4 text-center">Statut</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border/30">
                              {filteredContributions.map((cont) => (
                                <tr key={cont.id} className="hover:bg-muted/10 transition">
                                  <td className="py-4 px-6 align-top">
                                    <div className="font-bold text-foreground flex items-center gap-2">
                                      <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cont.authorColor }}></div>
                                      <span className="truncate max-w-[120px]">{cont.authorName}</span>
                                    </div>
                                    <div className="text-xs text-muted-foreground capitalize mt-1 flex gap-1 flex-wrap">
                                      <span className="border px-1 py-0.5 rounded-md">{cont.authorCategory}</span>
                                    </div>
                                  </td>
                                  <td className="py-4 px-4 align-top">
                                    <div className="relative inline-block max-w-[50ch] bg-indigo-50 border border-indigo-100 dark:bg-indigo-900/30 dark:border-indigo-800 rounded-2xl rounded-tl-sm p-3 shadow-sm text-sm leading-relaxed whitespace-pre-wrap break-words">
                                      "{cont.text}"
                                    </div>
                                  </td>
                                  <td className="py-4 px-4 text-center font-bold text-indigo-600 align-top">{cont.likes}</td>
                                  <td className="py-4 px-4 text-center align-top">
                                    <Badge variant="outline" className="text-emerald-600 bg-emerald-50">Validé</Badge>
                                  </td>
                                </tr>
                              ))}
                              {filteredContributions.length === 0 && (
                                <tr><td colSpan={4} className="py-10 text-center text-muted-foreground">Aucun commentaire pour cette question.</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <div className="p-10 text-center text-muted-foreground border rounded-xl shadow-sm bg-card">Sélectionnez une question précédente dans le panneau de gauche pour voir ses statistiques et synthèses.</div>
            )}
          </div>
        </div>
      </TabsContent>

      {/* -------------------- TAB 4: QUESTIONS PROPOSEES -------------------- */}
      <TabsContent value="proposed" className="space-y-6">
        <Card className="border-border shadow-sm">
          <CardHeader className="flex flex-row justify-between items-center bg-muted/20 border-b pb-4 mb-4">
            <div>
              <CardTitle className="text-lg font-display">Boîte à idées citoyennes</CardTitle>
              <CardDescription>Questions suggérées par les utilisateurs depuis le formulaire de participation.</CardDescription>
            </div>
            <div className="flex gap-2">
               <Select value={proposedFilter} onValueChange={setProposedFilter}>
                 <SelectTrigger className="w-[150px]"><SelectValue placeholder="Filtrer" /></SelectTrigger>
                 <SelectContent>
                    <SelectItem value="all">Tout</SelectItem>
                    <SelectItem value="week">Cette semaine</SelectItem>
                    <SelectItem value="month">Ce mois-ci</SelectItem>
                    <SelectItem value="year">Cette année</SelectItem>
                 </SelectContent>
               </Select>
               <Button variant="outline" size="sm" onClick={() => handleExportProposed('pdf')} className="text-red-600 gap-2"><FileText className="w-4 h-4"/> Exporter PDF</Button>
               <Button variant="outline" size="sm" onClick={() => handleExportProposed('word')} className="text-blue-600 gap-2"><File className="w-4 h-4"/> Exporter Word</Button>
               <Button variant="outline" size="sm" onClick={() => handleExportProposed('excel')} className="text-emerald-600 gap-2"><FileSpreadsheet className="w-4 h-4"/> Exporter Excel</Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
             <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-sm font-body">
                   <thead className="bg-muted/30 border-b border-border/50 text-muted-foreground">
                      <tr>
                         <th className="py-4 px-6 text-left">Auteur & Profil</th>
                         <th className="py-4 px-4 text-left">Suggéré le</th>
                         <th className="py-4 px-4 text-left">Domaine</th>
                         <th className="py-4 px-4 text-left">Question proposée</th>
                         <th className="py-4 px-6 text-right">Actions</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-border/30">
                      {filteredProposedQuestions.length > 0 ? filteredProposedQuestions.map((cont) => (
                         <tr key={cont.id} className="hover:bg-muted/10 transition">
                            <td className="py-4 px-6 align-top">
                               <div className="font-bold text-foreground flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cont.authorColor }}></div>
                                  <span className="truncate max-w-[120px]">{cont.authorName}</span>
                               </div>
                               <div className="text-xs text-muted-foreground capitalize mt-1 flex gap-1 flex-wrap">
                                  <span className="border px-1 py-0.5 rounded-md">{cont.authorCategory}</span>
                               </div>
                            </td>
                            <td className="py-4 px-4 align-top text-muted-foreground whitespace-nowrap">
                               {new Date(cont.createdAt).toLocaleDateString('fr-FR')} à {new Date(cont.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="py-4 px-4 align-top font-bold text-slate-700 dark:text-slate-300">
                               {cont.proposedDomain ? <Badge variant="secondary">{cont.proposedDomain}</Badge> : <span className="text-muted-foreground font-normal italic">Non précisé</span>}
                            </td>
                            <td className="py-4 px-4 align-top font-bold text-indigo-600 break-words max-w-[300px]">
                               {cont.proposedQuestion}
                            </td>
                            <td className="py-4 px-6 align-top text-right whitespace-nowrap">
                               <div className="flex justify-end gap-2">
                                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 font-bold text-white border-none" onClick={() => handleValidateProposedQuestion(cont)}>
                                     <Check className="w-4 h-4 mr-1"/> Valider
                                  </Button>
                                  <Button size="icon" variant="outline" onClick={() => handleEditProposedClick(cont)}>
                                     <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button size="icon" variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => handleDeleteProposedClick(cont.id)}>
                                     <Trash className="w-4 h-4" />
                                  </Button>
                               </div>
                            </td>
                         </tr>
                      )) : (
                        <tr>
                          <td colSpan={5} className="py-12 text-center text-muted-foreground border border-dashed rounded-3xl m-4">
                            Aucune question proposée pour l'instant.
                          </td>
                        </tr>
                      )}
                   </tbody>
                </table>
             </div>
          </CardContent>
        </Card>
      </TabsContent>

      <Dialog open={isEditDebateOpen} onOpenChange={setIsEditDebateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier la Question</DialogTitle>
            <DialogDescription>Modifiez les informations du débat en attente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4 max-h-[60vh] overflow-y-auto custom-scrollbar px-1">
            <div className="space-y-2">
              <Label>Titre (La question) *</Label>
              <Input value={editDebateTitle} onChange={e => setEditDebateTitle(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Description / Contexte *</Label>
              <Textarea value={editDebateDesc} onChange={e => setEditDebateDesc(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Titre (Arabe)</Label>
              <Input dir="rtl" value={editDebateTitleAr} onChange={e => setEditDebateTitleAr(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Description (Arabe)</Label>
              <Textarea dir="rtl" value={editDebateDescAr} onChange={e => setEditDebateDescAr(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDebateOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveDebateEdit} className="bg-indigo-600 hover:bg-indigo-700">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditProposedOpen} onOpenChange={setIsEditProposedOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier la Question Proposée</DialogTitle>
            <DialogDescription>Modifiez le texte et le domaine de la suggestion citoyenne.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Question *</Label>
              <Textarea value={editProposedText} onChange={e => setEditProposedText(e.target.value)} className="min-h-[100px]" />
            </div>
            <div className="space-y-2">
              <Label>Domaine *</Label>
              <Input value={editProposedDomain} onChange={e => setEditProposedDomain(e.target.value)} placeholder="Ex: Transport, Éducation..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditProposedOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveProposedEdit} className="bg-indigo-600 hover:bg-indigo-700">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TabsContent value="stats" className="space-y-6">
        <Card className="border-border shadow-sm">
          <CardHeader className="bg-muted/20 border-b pb-4 mb-4">
            <CardTitle className="text-lg font-display">Statistiques Globales des Débats</CardTitle>
            <CardDescription>Consultez les statistiques détaillées pour chaque question publiée et clôturée (par semaine, mois, année).</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
             <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-sm font-body">
                   <thead className="bg-muted/30 border-b border-border/50 text-muted-foreground">
                      <tr>
                         <th className="py-4 px-6 text-left">Période</th>
                         <th className="py-4 px-4 text-left">Débat</th>
                         <th className="py-4 px-4 text-center">Participants</th>
                         <th className="py-4 px-4 text-center">H/F</th>
                         <th className="py-4 px-4 text-center">Likes</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-border/30">
                      {debates.filter(d => d.status === 'publie' || d.status === 'cloture').sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map((d) => {
                         const dContribs = contributions.filter(c => c.debateId === d.id && c.status === "valide");
                         const h = dContribs.filter(c => c.genre === "homme").length;
                         const f = dContribs.filter(c => c.genre === "femme").length;
                         const likes = dContribs.reduce((acc, c) => acc + c.likes, 0);
                         const date = new Date(d.createdAt);
                         const yStr = date.getFullYear();
                         const mStr = date.toLocaleDateString('fr-FR', { month: 'long' });
                         const qNum = getQuestionOfMonth(date);
                         return (
                         <tr key={`stat_${d.id}`} className="hover:bg-muted/10 transition">
                            <td className="py-4 px-6 text-muted-foreground font-semibold capitalize whitespace-nowrap">
                               Sem. {qNum} - {mStr} {yStr}
                            </td>
                            <td className="py-4 px-4 font-bold text-foreground">
                               {d.title}
                            </td>
                            <td className="py-4 px-4 text-center text-indigo-600 font-bold">
                               {dContribs.length}
                            </td>
                            <td className="py-4 px-4 text-center text-muted-foreground whitespace-nowrap">
                               {h} H / {f} F
                            </td>
                            <td className="py-4 px-4 text-center text-emerald-600 font-bold">
                               {likes}
                            </td>
                         </tr>
                      )})}
                      {debates.filter(d => d.status === 'publie' || d.status === 'cloture').length === 0 && (
                        <tr>
                          <td colSpan={5} className="py-12 text-center text-muted-foreground border border-dashed rounded-3xl m-4">
                            Aucun débat avec des données statistiques pour l'instant.
                          </td>
                        </tr>
                      )}
                   </tbody>
                </table>
             </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
